import { useState, type FC } from 'react';
import { useAuth } from '../../hooks/useAuth';

export type AccountMenuProps = {
  onNavigate: (view: string) => void;
};

/**
 * Compact account control for platform shells: sign-in, profile shortcut, rewards hint, sign-out.
 * Uses app view ids (see App.tsx navigate), not URL paths.
 */
export const AccountMenu: FC<AccountMenuProps> = ({ onNavigate }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (!isAuthenticated || !user) {
    return (
      <button
        type="button"
        onClick={() => onNavigate('auth')}
        className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-medium text-white/90 transition-colors hover:bg-white/10"
      >
        Sign in
      </button>
    );
  }

  const pts = user.profile?.rewardsPoints ?? 0;
  const label =
    user.profile?.displayName?.trim() || user.name?.trim() || user.email?.split('@')[0] || '?';
  const initials = label.slice(0, 1).toUpperCase();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/40 bg-slate-900 text-sm font-semibold text-amber-300"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {initials}
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-52 rounded-lg border border-white/15 bg-slate-950 py-1 text-sm shadow-xl"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNavigate('profile');
            }}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-white/90 hover:bg-white/10"
          >
            Profile
            <span className="text-xs text-amber-400/90">{pts} pts</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="w-full px-3 py-2 text-left text-white/70 hover:bg-white/10"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};
